import sharedConstants from './shared_constants.json';
import {
  MIN_EFFECTIVE_MARKUP_MULTIPLIER,
  TOKENS_PER_USD,
  costUsdToBilledTokens,
  videoCostUsdToTokens,
} from './tokenBilling';

/**
 * Convert a raw USD provider cost into the tokens we bill for it.
 *
 * Delegates to `tokenBilling.ts`, which is the port of
 * `constants/pricing/base.py::cost_usd_to_billed_tokens` -- markup AND the margin
 * floor, in the one place that knows about both.
 *
 * It used to be `Math.ceil(usd * TOKENS_PER_USD * VIDEO_GEN_MARKUP)` written out
 * here. That is the bypass shape: it applies a markup and skips the floor, so the
 * moment a domain markup sits below MIN_EFFECTIVE_MARKUP_MULTIPLIER the quote and
 * the charge part company silently.
 */
export function usdToTokens(usd: number): number {
  return videoCostUsdToTokens(usd);
}

/** Format token amount for display (e.g., "225 tokens") */
export function formatTokens(tokens: number): string {
  return `${tokens.toLocaleString()} tokens`;
}

export const VIDEO_SOURCES = {
  // Free Local Providers
  STOCK: 'stock',
  AI_WAN21: 'ai_wan21',
  AI_LTX2: 'ai_ltx2',
  AI_ANIMATEDIFF: 'ai_animatediff',
  AI_DEFORUM: 'ai_deforum',
  YOUTUBE: 'youtube',

  AI_WAN_VACE: 'ai_wan_vace',

  // Cloud T2V Providers
  AI_VEO: 'ai_veo',
  AI_KLING: 'ai_kling',
  AI_VIDU: 'ai_vidu',
  AI_WAN26_T2V: 'ai_wan26_t2v',

  // Cloud I2V Providers
  AI_WAN26_I2V: 'ai_wan26_i2v',
  AI_WAN25_I2V: 'ai_wan25_i2v',
  AI_KLING_I2V_PRO: 'ai_kling_i2v_pro',
  AI_SEEDANCE_I2V: 'ai_seedance_i2v',
  AI_SORA2_I2V: 'ai_sora2_i2v',
  AI_SORA2_PRO_I2V: 'ai_sora2_pro_i2v',
  AI_FRAMEPACK: 'ai_framepack',

  // Seedance 2.0 (ByteDance via fal.ai)
  AI_SEEDANCE2: 'ai_seedance2',

  // AI Director (smart auto-selection)
  AI_DIRECTOR: 'ai_director',

  // Special Feature Providers
  AI_INFINITETALK: 'ai_infinitetalk',
  AI_KLING_MOTION: 'ai_kling_motion',
  AI_KLING_O1_R2V: 'ai_kling_o1_r2v',

  // Local Wan Providers (Phase 4 — schema registered in Phase 1)
  AI_WAN21_T2V_1_3B: 'ai_wan21_t2v_1_3b',
  AI_WAN22_TI2V_5B: 'ai_wan22_ti2v_5b',
  AI_WAN22_TI2V_5B_I2V: 'ai_wan22_ti2v_5b_i2v',
  AI_WAN22_T2V_14B: 'ai_wan22_t2v_14b',
} as const;

export type VideoSource = (typeof VIDEO_SOURCES)[keyof typeof VIDEO_SOURCES];

/** Available stock video providers */
export type StockVideoProvider = 'pexels' | 'pixabay';

/** Available AI video providers */
export type AIVideoProvider =
  | 'veo'
  | 'kling'
  | 'vidu'
  | 'wan21'
  | 'wan_vace'
  | 'ltx2'
  | 'animatediff'
  | 'deforum'
  | 'wan26_t2v'
  | 'wan26_i2v'
  | 'wan25_i2v'
  | 'kling_i2v_pro'
  | 'seedance_i2v'
  | 'sora2_i2v'
  | 'sora2_pro_i2v'
  | 'framepack'
  | 'infinitetalk'
  | 'kling_motion'
  | 'kling_o1_r2v'
  | 'seedance2';

/** Video source categories for grouping */
export type VideoSourceCategory = 'free_local' | 'cloud_t2v' | 'cloud_i2v' | 'special' | 'meta';

/** Video generation type */
export type VideoGenerationType =
  | 't2v'
  | 'i2v'
  | 'talking_head'
  | 'motion_transfer'
  | 'multi_reference';

/** Quality tier */
export type VideoQualityTier = 'good' | 'great' | 'excellent' | 'premium';

/** Video source detailed info */
export interface VideoSourceInfo {
  name: string;
  description: string;
  type: VideoGenerationType;
  category: VideoSourceCategory;
  costPer5Sec: number;
  quality: VideoQualityTier;
  requiresImage: boolean;
  requiresAudio?: boolean;
  requiresReferenceVideo?: boolean;
  maxImages?: number;
  isLocal: boolean;
  /** True when the provider is planned but not yet implemented locally. */
  comingSoon?: boolean;
}

export interface VideoProviderOption {
  value: string;
  labelKey: string;
  descriptionKey?: string;
  enabled: boolean;
  pricing?: string;
  speed?: string;
  badge?: string;
  experimental?: boolean;
  /** True when the provider is planned but not yet available. */
  comingSoon?: boolean;
}

/** Detailed info for all video sources */
export const VIDEO_SOURCE_INFO: Record<VideoSource, VideoSourceInfo> = {
  // Free Local Providers
  [VIDEO_SOURCES.STOCK]: {
    name: 'Stock Videos',
    description: 'Professional video footage from Pexels & Pixabay',
    type: 't2v',
    category: 'free_local',
    costPer5Sec: 0,
    quality: 'good',
    requiresImage: false,
    isLocal: false,
  },
  [VIDEO_SOURCES.AI_WAN21]: {
    name: 'Wan 2.1 (Local)',
    description: 'High-quality T2V/I2V - runs locally on your GPU',
    type: 't2v',
    category: 'free_local',
    costPer5Sec: 0,
    quality: 'excellent',
    requiresImage: false,
    isLocal: false,
    comingSoon: true,
  },
  [VIDEO_SOURCES.AI_LTX2]: {
    name: 'LTX-2 (Local)',
    description: 'Ultra-fast T2V/I2V - faster than real-time, native 4K',
    type: 't2v',
    category: 'free_local',
    costPer5Sec: 0,
    quality: 'great',
    requiresImage: false,
    isLocal: false,
    comingSoon: true,
  },
  [VIDEO_SOURCES.AI_ANIMATEDIFF]: {
    name: 'AnimateDiff (Local)',
    description: 'AnimateDiff SD 1.5 via local ComfyUI - Free, ~60s per 16-frame video',
    type: 'i2v',
    category: 'free_local',
    costPer5Sec: 0,
    quality: 'good',
    requiresImage: true,
    isLocal: true,
  },
  [VIDEO_SOURCES.AI_DEFORUM]: {
    name: 'Deforum (Local)',
    description: 'Schedule-driven AI animation (camera + prompt morphing) via local ComfyUI - Free',
    type: 't2v',
    category: 'free_local',
    costPer5Sec: 0,
    quality: 'good',
    requiresImage: false,
    isLocal: true,
  },
  [VIDEO_SOURCES.AI_WAN_VACE]: {
    name: 'Wan VACE (Local)',
    description: 'All-in-one video creation & editing - T2V, I2V, R2V, inpainting, pose',
    type: 't2v',
    category: 'free_local',
    costPer5Sec: 0,
    quality: 'excellent',
    requiresImage: false,
    isLocal: false,
    comingSoon: true,
  },
  [VIDEO_SOURCES.YOUTUBE]: {
    name: 'YouTube Clips',
    description: 'Curated clips from YouTube',
    type: 't2v',
    category: 'free_local',
    costPer5Sec: 0,
    quality: 'good',
    requiresImage: false,
    isLocal: false,
  },

  // Cloud T2V Providers
  [VIDEO_SOURCES.AI_VEO]: {
    name: 'Google Veo 3.1',
    description: 'Highest quality AI video generation',
    type: 't2v',
    category: 'cloud_t2v',
    costPer5Sec: 0.75,
    quality: 'premium',
    requiresImage: false,
    isLocal: false,
  },
  [VIDEO_SOURCES.AI_KLING]: {
    name: 'Kling AI',
    description: 'Great quality via fal.ai - excellent value',
    type: 't2v',
    category: 'cloud_t2v',
    costPer5Sec: 0.35,
    quality: 'excellent',
    requiresImage: false,
    isLocal: false,
  },
  [VIDEO_SOURCES.AI_VIDU]: {
    name: 'Vidu Q3',
    description: 'AI video with native audio (dialogue + SFX)',
    type: 't2v',
    category: 'cloud_t2v',
    costPer5Sec: 0.8,
    quality: 'excellent',
    requiresImage: false,
    isLocal: false,
  },
  [VIDEO_SOURCES.AI_WAN26_T2V]: {
    name: 'WAN 2.6 (Cloud)',
    description: 'Latest WAN model via RunPod',
    type: 't2v',
    category: 'cloud_t2v',
    costPer5Sec: 0.5,
    quality: 'excellent',
    requiresImage: false,
    isLocal: false,
  },
  [VIDEO_SOURCES.AI_SEEDANCE2]: {
    name: 'Seedance 2.0',
    description: 'Top-rated ByteDance AI video (T2V/I2V/R2V) with free native audio',
    type: 't2v',
    category: 'cloud_t2v',
    costPer5Sec: 1.21,
    quality: 'premium',
    requiresImage: false,
    isLocal: false,
  },

  // Cloud I2V Providers
  [VIDEO_SOURCES.AI_WAN26_I2V]: {
    name: 'WAN 2.6 I2V',
    description: 'Best quality image-to-video',
    type: 'i2v',
    category: 'cloud_i2v',
    costPer5Sec: 0.5,
    quality: 'excellent',
    requiresImage: true,
    isLocal: false,
  },
  [VIDEO_SOURCES.AI_WAN25_I2V]: {
    name: 'WAN 2.5 I2V',
    description: 'Lip-sync support for talking videos',
    type: 'i2v',
    category: 'cloud_i2v',
    costPer5Sec: 0.5,
    quality: 'great',
    requiresImage: true,
    isLocal: false,
  },
  [VIDEO_SOURCES.AI_KLING_I2V_PRO]: {
    name: 'Kling v2.1 I2V Pro',
    description: 'Cinematic image-to-video',
    type: 'i2v',
    category: 'cloud_i2v',
    costPer5Sec: 0.45,
    quality: 'excellent',
    requiresImage: true,
    isLocal: false,
  },
  [VIDEO_SOURCES.AI_SEEDANCE_I2V]: {
    name: 'Seedance V1.5 Pro',
    description: 'Smooth frame transitions',
    type: 'i2v',
    category: 'cloud_i2v',
    costPer5Sec: 0.18,
    quality: 'excellent',
    requiresImage: true,
    isLocal: false,
  },
  [VIDEO_SOURCES.AI_SORA2_I2V]: {
    name: 'SORA 2 I2V',
    description: 'OpenAI premium quality',
    type: 'i2v',
    category: 'cloud_i2v',
    costPer5Sec: 0.5,
    quality: 'premium',
    requiresImage: true,
    isLocal: false,
  },
  [VIDEO_SOURCES.AI_SORA2_PRO_I2V]: {
    name: 'SORA 2 Pro I2V',
    description: 'Highest quality from OpenAI',
    type: 'i2v',
    category: 'cloud_i2v',
    costPer5Sec: 0.8,
    quality: 'premium',
    requiresImage: true,
    isLocal: false,
  },
  [VIDEO_SOURCES.AI_FRAMEPACK]: {
    name: 'FramePack',
    description: 'AI image-to-video generation via local GPU or cloud',
    type: 'i2v',
    category: 'cloud_i2v',
    costPer5Sec: 0.1,
    quality: 'great',
    requiresImage: true,
    isLocal: false,
  },

  // AI Director (meta)
  [VIDEO_SOURCES.AI_DIRECTOR]: {
    name: 'AI Director',
    description: 'LLM auto-selects best media per scene',
    type: 't2v',
    category: 'meta',
    costPer5Sec: 2.0, // Ceiling based on Veo Standard ($0.40/s * 5s = $2.00)
    quality: 'premium',
    requiresImage: false,
    isLocal: false,
  },

  // Special Feature Providers
  [VIDEO_SOURCES.AI_INFINITETALK]: {
    name: 'InfiniteTalk',
    description: 'Animate portraits with audio - talking heads',
    type: 'talking_head',
    category: 'special',
    costPer5Sec: 0.1,
    quality: 'good',
    requiresImage: true,
    requiresAudio: true,
    isLocal: false,
  },
  [VIDEO_SOURCES.AI_KLING_MOTION]: {
    name: 'Kling Motion Control',
    description: 'Motion transfer from reference video',
    type: 'motion_transfer',
    category: 'special',
    costPer5Sec: 0.25,
    quality: 'excellent',
    requiresImage: true,
    requiresReferenceVideo: true,
    isLocal: false,
  },
  [VIDEO_SOURCES.AI_KLING_O1_R2V]: {
    name: 'Kling O1 R2V',
    description: 'Multi-reference video generation (1-4 images)',
    type: 'i2v',
    category: 'cloud_i2v',
    costPer5Sec: 0.3,
    quality: 'excellent',
    requiresImage: true,
    maxImages: 4,
    isLocal: false,
  },

  // Local Wan Providers (Phase 4)
  [VIDEO_SOURCES.AI_WAN21_T2V_1_3B]: {
    name: 'Wan 2.1 1.3B (Local)',
    description: 'Lightweight local T2V — 480p, ~30 steps, 8 GB VRAM',
    type: 't2v',
    category: 'free_local',
    costPer5Sec: 0,
    quality: 'good',
    requiresImage: false,
    isLocal: true,
  },
  [VIDEO_SOURCES.AI_WAN22_TI2V_5B]: {
    name: 'Wan 2.2 5B (Local T2V)',
    description: 'Versatile local T2V — 720p, 12 GB VRAM',
    type: 't2v',
    category: 'free_local',
    costPer5Sec: 0,
    quality: 'excellent',
    requiresImage: false,
    isLocal: true,
  },
  [VIDEO_SOURCES.AI_WAN22_TI2V_5B_I2V]: {
    name: 'Wan 2.2 5B I2V (Local)',
    description: 'Native local Image-to-Video — 720p, 12 GB VRAM',
    type: 'i2v',
    category: 'free_local',
    costPer5Sec: 0,
    quality: 'excellent',
    requiresImage: true,
    isLocal: true,
  },
  [VIDEO_SOURCES.AI_WAN22_T2V_14B]: {
    name: 'Wan 2.2 14B (Local)',
    description: 'Premium SOTA local T2V — 4-step Lightning, 20 GB VRAM',
    type: 't2v',
    category: 'free_local',
    costPer5Sec: 0,
    quality: 'premium',
    requiresImage: false,
    isLocal: true,
  },
};

/** Stock video provider options */
export const STOCK_VIDEO_PROVIDERS: VideoProviderOption[] = [
  { value: 'pexels', labelKey: 'videoSource.provider.pexels', enabled: true, pricing: 'Free' },
  { value: 'pixabay', labelKey: 'videoSource.provider.pixabay', enabled: true, pricing: 'Free' },
];

/** AI video provider options */
export const AI_VIDEO_PROVIDERS: VideoProviderOption[] = [
  {
    value: 'veo',
    labelKey: 'videoSource.provider.veo',
    enabled: true,
    pricing: '225-600 tokens/scene',
  },
  {
    value: 'kling',
    labelKey: 'videoSource.provider.kling',
    enabled: true,
    pricing: '525-1,680 tokens/scene',
  },
  {
    value: 'wan_vace',
    labelKey: 'videoSource.provider.wanVace',
    enabled: true,
    pricing: 'Free',
  },
  {
    value: 'wan21',
    labelKey: 'videoSource.provider.wan21',
    enabled: true,
    pricing: 'Free',
  },
  {
    value: 'ltx2',
    labelKey: 'videoSource.provider.ltx2',
    enabled: true,
    pricing: 'Free',
  },
  {
    value: 'animatediff',
    labelKey: 'videoSource.provider.animatediff',
    enabled: true,
    pricing: 'Free',
  },
  {
    value: 'animatediff-comfyui',
    labelKey: 'videoSource.provider.animatediffComfyui',
    descriptionKey: 'videoSource.providerDescription.animatediffComfyui',
    enabled: true,
    pricing: 'Free',
    speed: 'slow',
    badge: 'EXPERIMENTAL',
    experimental: true,
  },
];

/** Default stock video provider */
export const DEFAULT_STOCK_VIDEO_PROVIDER: StockVideoProvider = 'pexels';

/** Default AI video provider */
export const DEFAULT_AI_VIDEO_PROVIDER: AIVideoProvider = 'veo';

/** Video source option for UI display */
export interface VideoSourceOption {
  value: VideoSource;
  label: string;
  description: string;
  icon: string;
  pricing: string;
  badge: string | null;
  category: VideoSourceCategory;
  providers: VideoProviderOption[];
  requiresImage?: boolean;
  requiresAudio?: boolean;
  requiresReferenceVideo?: boolean;
  /** True when the provider is planned but not yet available. */
  comingSoon?: boolean;
  /** Subjective output-quality tier, 1 (basic) to 5 (state-of-the-art). */
  quality?: 1 | 2 | 3 | 4 | 5;
  /** Whether this is the recommended default for the collapsed preview. */
  recommended?: boolean;
}

/** Category labels for UI grouping */
export const VIDEO_SOURCE_CATEGORY_LABELS: Record<VideoSourceCategory, string> = {
  free_local: 'Free (Local)',
  cloud_t2v: 'Cloud T2V (Paid)',
  cloud_i2v: 'Cloud I2V (Paid)',
  special: 'Special Features',
  meta: 'Smart Selection',
};

// VIDEO_SOURCE_OPTIONS is rendered in array order by AIVideoProviderPanel.
// Within each section (T2V / I2V), cards are ordered by ascending real cost
// (see costPer5Sec in VIDEO_SOURCE_INFO above). Pricing strings are intentionally
// empty for paid providers — the precise token count is shown by TokenEstimateCard
// once the user picks a duration. Free providers keep the "Free" label.
export const VIDEO_SOURCE_OPTIONS: VideoSourceOption[] = [
  // ===== FREE LOCAL PROVIDERS =====
  {
    value: VIDEO_SOURCES.STOCK,
    label: 'Stock Videos',
    description: 'Professional video footage',
    icon: 'Film',
    pricing: 'Free',
    badge: null,
    category: 'free_local',
    providers: STOCK_VIDEO_PROVIDERS,
    quality: 3,
    recommended: true,
  },
  {
    value: VIDEO_SOURCES.YOUTUBE,
    label: 'YouTube Clips',
    description: 'Curated clips from YouTube',
    icon: 'Youtube',
    pricing: 'Free',
    badge: null,
    category: 'free_local',
    providers: [],
    quality: 3,
  },
  {
    value: VIDEO_SOURCES.AI_ANIMATEDIFF,
    label: 'AnimateDiff (Local)',
    description: 'Add motion to images',
    icon: 'Wand2',
    pricing: 'Free',
    badge: 'LOCAL',
    category: 'free_local',
    providers: [],
    requiresImage: true,
    quality: 2,
  },
  {
    value: VIDEO_SOURCES.AI_DEFORUM,
    label: 'Deforum (Local)',
    description: 'Schedule-driven camera + prompt morphing animation',
    icon: 'Wand2',
    pricing: 'Free',
    badge: 'NEW',
    category: 'free_local',
    providers: [],
    quality: 2,
  },

  // ===== LOCAL WAN PROVIDERS (Phase 4) =====
  {
    value: VIDEO_SOURCES.AI_WAN21_T2V_1_3B,
    label: 'Wan 2.1 1.3B (Local)',
    description: 'Lightweight T2V — 480p, 8 GB VRAM',
    icon: 'Cpu',
    pricing: 'Free',
    badge: 'LOCAL',
    category: 'free_local',
    providers: [],
    quality: 3,
  },
  {
    value: VIDEO_SOURCES.AI_WAN22_TI2V_5B,
    label: 'Wan 2.2 5B (Local T2V)',
    description: 'Versatile T2V — 720p, 12 GB VRAM',
    icon: 'Cpu',
    pricing: 'Free',
    badge: 'LOCAL',
    category: 'free_local',
    providers: [],
    quality: 4,
  },
  {
    value: VIDEO_SOURCES.AI_WAN22_TI2V_5B_I2V,
    label: 'Wan 2.2 5B I2V (Local)',
    description: 'Native I2V — 720p, 12 GB VRAM',
    icon: 'Cpu',
    pricing: 'Free',
    badge: 'LOCAL',
    category: 'free_local',
    providers: [],
    requiresImage: true,
    quality: 4,
  },
  {
    value: VIDEO_SOURCES.AI_WAN22_T2V_14B,
    label: 'Wan 2.2 14B (Local)',
    description: 'Premium SOTA T2V — 4-step Lightning, 20 GB VRAM',
    icon: 'Cpu',
    pricing: 'Free',
    badge: 'LOCAL',
    category: 'free_local',
    providers: [],
    quality: 5,
  },

  // ===== CLOUD T2V PROVIDERS (ascending by costPer5Sec) =====
  {
    value: VIDEO_SOURCES.AI_KLING,
    label: 'Kling AI',
    description: 'Great quality via fal.ai',
    icon: 'Sparkles',
    pricing: '',
    badge: null,
    category: 'cloud_t2v',
    providers: [],
    quality: 4,
  },
  {
    value: VIDEO_SOURCES.AI_WAN26_T2V,
    label: 'WAN 2.6 (Cloud)',
    description: 'Latest WAN model',
    icon: 'Cloud',
    pricing: '',
    badge: null,
    category: 'cloud_t2v',
    providers: [],
    quality: 4,
  },
  {
    value: VIDEO_SOURCES.AI_VEO,
    label: 'Google Veo 3.1',
    description: 'Highest quality AI video',
    icon: 'Sparkles',
    pricing: '',
    badge: null,
    category: 'cloud_t2v',
    providers: [],
    quality: 5,
  },
  {
    value: VIDEO_SOURCES.AI_VIDU,
    label: 'Vidu Q3',
    description: 'AI video with native audio',
    icon: 'Music',
    pricing: '',
    badge: null,
    category: 'cloud_t2v',
    providers: [],
    quality: 4,
  },
  {
    value: VIDEO_SOURCES.AI_SEEDANCE2,
    label: 'Seedance 2.0',
    description: 'Top-rated ByteDance AI video (T2V / I2V / Reference-to-Video)',
    icon: 'Sparkles',
    pricing: '',
    badge: null,
    category: 'cloud_t2v',
    providers: [],
    quality: 5,
  },

  // ===== CLOUD I2V PROVIDERS (ascending by costPer5Sec) =====
  {
    value: VIDEO_SOURCES.AI_FRAMEPACK,
    label: 'FramePack',
    description: 'Image-to-video via GPU',
    icon: 'Wand2',
    pricing: '',
    badge: null,
    category: 'cloud_i2v',
    providers: [],
    requiresImage: true,
    quality: 3,
  },
  {
    value: VIDEO_SOURCES.AI_SEEDANCE_I2V,
    label: 'Seedance V1.5 Pro',
    description: 'Smooth frame transitions',
    icon: 'ImagePlus',
    pricing: '',
    badge: null,
    category: 'cloud_i2v',
    providers: [],
    requiresImage: true,
    quality: 4,
  },
  {
    value: VIDEO_SOURCES.AI_KLING_O1_R2V,
    label: 'Kling O1 R2V',
    description: 'Multi-reference video (1-4 images)',
    icon: 'Layers',
    pricing: '',
    badge: null,
    category: 'cloud_i2v',
    providers: [],
    requiresImage: true,
    quality: 4,
  },
  {
    value: VIDEO_SOURCES.AI_KLING_I2V_PRO,
    label: 'Kling v2.1 I2V Pro',
    description: 'Cinematic quality',
    icon: 'ImagePlus',
    pricing: '',
    badge: null,
    category: 'cloud_i2v',
    providers: [],
    requiresImage: true,
    quality: 4,
  },
  {
    value: VIDEO_SOURCES.AI_WAN26_I2V,
    label: 'WAN 2.6 I2V',
    description: 'Best quality I2V',
    icon: 'ImagePlus',
    pricing: '',
    badge: null,
    category: 'cloud_i2v',
    providers: [],
    requiresImage: true,
    quality: 4,
  },
  {
    value: VIDEO_SOURCES.AI_WAN25_I2V,
    label: 'WAN 2.5 I2V',
    description: 'Lip-sync support',
    icon: 'ImagePlus',
    pricing: '',
    badge: null,
    category: 'cloud_i2v',
    providers: [],
    requiresImage: true,
    quality: 3,
  },
  {
    value: VIDEO_SOURCES.AI_SORA2_I2V,
    label: 'SORA 2 I2V',
    description: 'OpenAI premium quality',
    icon: 'Star',
    pricing: '',
    badge: null,
    category: 'cloud_i2v',
    providers: [],
    requiresImage: true,
    quality: 5,
  },
  {
    value: VIDEO_SOURCES.AI_SORA2_PRO_I2V,
    label: 'SORA 2 Pro I2V',
    description: 'Highest quality from OpenAI',
    icon: 'Crown',
    pricing: '',
    badge: null,
    category: 'cloud_i2v',
    providers: [],
    requiresImage: true,
    quality: 5,
  },

  // ===== SPECIAL FEATURE PROVIDERS =====
  {
    value: VIDEO_SOURCES.AI_INFINITETALK,
    label: 'InfiniteTalk',
    description: 'Animate portraits with audio',
    icon: 'Mic',
    pricing: '',
    badge: null,
    category: 'special',
    providers: [],
    requiresImage: true,
    requiresAudio: true,
    quality: 3,
  },
  {
    value: VIDEO_SOURCES.AI_KLING_MOTION,
    label: 'Kling Motion Control',
    description: 'Motion transfer',
    icon: 'Move',
    pricing: '',
    badge: null,
    category: 'special',
    providers: [],
    requiresImage: true,
    requiresReferenceVideo: true,
    quality: 4,
  },

  // ===== AI DIRECTOR (META) =====
  {
    value: VIDEO_SOURCES.AI_DIRECTOR,
    label: 'AI Director',
    description: 'Smart auto-selection per scene',
    icon: 'Brain',
    pricing: '',
    badge: null,
    category: 'meta',
    providers: [],
    quality: 5,
  },
];

/**
 * What Google bills us per second of Veo 3.1, by quality and by the resolution
 * ROW the server actually charges at.
 *
 * Mirrors `VEO_PRICING_PER_SECOND_BY_RESOLUTION` in
 * `constants/video_generation/veo.py`, re-fetched 2026-09-01 from
 * https://ai.google.dev/gemini-api/docs/pricing (with-audio column, because
 * VEO_AUDIO_ENABLED_DEFAULT is on).
 *
 * THREE ROWS. It carried two while `calculate_veo_cost()` took a boolean
 * `upscale_4k`, which could only say "4K or not" and so costed every 720p job at
 * the 1080p rate. The backend now takes the resolution itself, and Google really
 * does charge less for 720p ($0.10 vs $0.12 on Fast), so the quote follows.
 *
 * The version before that carried `price: 0.15` for Fast and modelled 4K as a
 * flat `extraCost: 0.5 / extraTokens: 750` add-on. Google has no such add-on --
 * 4K is its own per-second rate -- and 0.15 was a rate nobody charged at either
 * end.
 */
const VEO_COST_USD_PER_SECOND: Record<
  'fast' | 'standard',
  Record<'720p' | '1080p' | '4k', number>
> = {
  fast: { '720p': 0.1, '1080p': 0.12, '4k': 0.3 },
  standard: { '720p': 0.4, '1080p': 0.4, '4k': 0.6 },
};

export const VEO_QUALITY_OPTIONS = [
  { value: 'fast', label: 'Fast', description: '~30s generation' },
  {
    value: 'standard',
    label: 'Standard',
    description: '~60s generation, higher quality',
    warning: 'Not recommended — only 1-8% quality improvement for 3.3x the cost',
  },
] as const;

export const VEO_RESOLUTION_OPTIONS = [
  { value: '720p', label: '720p HD' },
  { value: '1080p', label: '1080p Full HD' },
  { value: '4k', label: '4K Ultra HD' },
] as const;

export const VEO_ASPECT_RATIO_OPTIONS = [
  { value: '16:9', label: 'Landscape (16:9)', icon: 'RectangleHorizontal' },
  { value: '9:16', label: 'Portrait (9:16)', icon: 'RectangleVertical' },
  { value: '1:1', label: 'Square (1:1)', icon: 'Square' },
] as const;

export type VeoQuality = (typeof VEO_QUALITY_OPTIONS)[number]['value'];
export type VeoResolution = (typeof VEO_RESOLUTION_OPTIONS)[number]['value'];
export type VeoAspectRatio = (typeof VEO_ASPECT_RATIO_OPTIONS)[number]['value'];

export interface VeoConfig {
  quality: VeoQuality;
  resolution: VeoResolution;
  aspect_ratio: VeoAspectRatio;
  duration: number;
  enable_audio: boolean;
}

export const DEFAULT_VEO_CONFIG: VeoConfig = {
  quality: 'fast',
  resolution: '1080p',
  aspect_ratio: '16:9',
  duration: 8,
  enable_audio: true,
};

/**
 * What Google bills US for a Veo clip, in USD. Vendor cost, not the user's price.
 *
 * Mirrors `constants/video_generation/veo.py::calculate_veo_cost`, which now
 * reads the resolution itself rather than a `upscale_4k` boolean -- so all three
 * of Google's rows are reachable and a 720p clip is costed at 720p.
 */
export function calculateVeoCost(
  durationSeconds: number,
  quality: VeoQuality,
  resolution: VeoResolution
): number {
  return durationSeconds * VEO_COST_USD_PER_SECOND[quality][resolution];
}

/**
 * Tokens a Veo clip is charged, matching
 * `VideoGenerationPricing.estimate_veo(duration, quality, resolution)`.
 */
export function calculateVeoTokens(
  durationSeconds: number,
  quality: VeoQuality,
  resolution: VeoResolution
): number {
  const costUsd = calculateVeoCost(durationSeconds, quality, resolution);
  if (quality !== 'standard') return usdToTokens(costUsd);

  // Veo Standard is the ONE SKU priced by hand rather than by the domain markup.
  // It cannot be won on price at any markup -- Google sells the same model through
  // Flow at $1.00-$2.50 while charging us $4.00 per 10s -- so the owner set 1.1x
  // cost at the deepest volume discount on 2026-09-02, about 9% gross margin.
  //
  // Mirrors VEO_STANDARD_MIN_MARGIN_MULTIPLIER in constants/video_generation/veo.py.
  // The generated shared_constants.json carries no VEO_* pricing key, so this is a
  // transcription, and the only thing holding the two equal is
  // tests/test_frontend/test_frontend_video_prices_match_backend_regression.py --
  // which compiles this file and compares every quote against the server. If you
  // change one side, that test names the numbers that went stale on the other.
  // list->deepest-discount ratio, derived rather than transcribed: the exported
  // MIN_EFFECTIVE_MARKUP_MULTIPLIER already equals MIN_MARGIN_MULTIPLIER x that
  // ratio, so dividing it out gives the ratio without a second copy of $0.0007.
  const listToDeepest = MIN_EFFECTIVE_MARKUP_MULTIPLIER / SYSTEM_MIN_MARGIN_MULTIPLIER;
  return Math.ceil(costUsd * VEO_STANDARD_MIN_MARGIN_MULTIPLIER * listToDeepest * TOKENS_PER_USD);
}

/**
 * The margin Veo Standard is priced to, in multiples of what Vertex bills us.
 * Transcribed from VEO_STANDARD_MIN_MARGIN_MULTIPLIER; see calculateVeoTokens.
 */
export const VEO_STANDARD_MIN_MARGIN_MULTIPLIER = 1.1;

/** MIN_MARGIN_MULTIPLIER from constants/pricing/base.py -- the floor Standard is excepted from. */
const SYSTEM_MIN_MARGIN_MULTIPLIER = 1.25;

/**
 * Billed tokens per second of Veo at this quality and resolution.
 *
 * For the picker's "N tokens/s" caption only. It is resolution-dependent -- 4K is
 * its own per-second rate, not a flat surcharge -- so there is no single
 * per-quality number to hang on VEO_QUALITY_OPTIONS, which is why the field that
 * used to be there is gone.
 */
export function veoTokensPerSecond(quality: VeoQuality, resolution: VeoResolution): number {
  return calculateVeoTokens(1, quality, resolution);
}

// =============================================================================
// SINGLE-SHOT CLOUD SOURCE TOKEN CALCULATORS
//
// Every vendor price below is READ from the generated `shared_constants.json`,
// which `npm run generate:all` rebuilds from `constants/pricing/public_endpoints.py`
// before every type-check, dev server and build. There are no transcribed vendor
// numbers in this section, so the cost half of each quote cannot go stale.
//
// The block this replaces returned those vendor rows AS the user's price -- 900
// tokens for a $0.90 Kling clip, 1000 for a $1.00 WAN clip -- which was exactly
// the defect `estimate_runpod_public` was fixed for on the server: the rows are
// what we PAY. Read through `usdToTokens` they become what we CHARGE, and the
// frontend quote matches the server's charge again.
//
// WHY MOST OF THESE TAKE NO DURATION
// ----------------------------------
// The scene strategies call these services with no duration and no size, so the
// job runs at the service's own default and the server prices it at exactly that
// (`RUNPOD_PUBLIC_SINGLE_SHOT_RUN_PARAMS` in constants/pricing/video_generation.py).
// A duration argument here would be a knob that changes the quote without
// changing the bill. The previous signatures took one, and SceneTab passed a flat
// 5 s into all of them -- right by coincidence for five of the seven and wrong for
// the two Sora sources, whose real run is 4 s.
//
// Everything in this section is pinned against the live backend estimators by
// tests/test_frontend/test_frontend_video_prices_match_backend_regression.py.
// =============================================================================

const PUBLIC_ENDPOINT_COSTS = sharedConstants.constants.urls;
const PRICING_MISC = sharedConstants.constants.misc;

/**
 * Vendor price rows in `public_endpoints.py` are stated in token units at that
 * module's own "tokens = cost_usd * 1000" convention. This recovers the dollars.
 */
function vendorCostUsd(tokenUnits: number): number {
  return tokenUnits / TOKENS_PER_USD;
}

/**
 * The duration each single-shot service actually runs at, in seconds.
 *
 * Mirrors `RUNPOD_PUBLIC_SINGLE_SHOT_RUN_PARAMS` (constants/pricing/video_generation.py),
 * which is not exported to shared_constants.json; the regression test pins it.
 * These are the SERVICES' own defaults, not a preference of ours -- changing a
 * number here does not change what runs, it only breaks the quote.
 *
 * Providers whose vendor row is a per-duration TABLE index it with the literal key
 * ('5', '4') rather than reading it back out of here, because a computed key
 * would erase the JSON's own key types and with them the compile error you want
 * when a vendor drops a duration row. The two are the same number, and
 * test_frontend_single_shot_run_seconds_match_the_backend plus the quote/charge
 * comparison check both halves.
 */
const SINGLE_SHOT_RUN_SECONDS = {
  wan26I2v: 5,
  wan25I2v: 5,
  klingI2vPro: 5,
  klingO1R2v: 5,
  seedanceI2v: 5,
  sora2I2v: 4,
  sora2ProI2v: 4,
} as const;

/** WAN 2.6 I2V, one clip at the service default (5 s, 720p). */
export function calculateWan26I2vTokens(): number {
  return usdToTokens(
    vendorCostUsd(PUBLIC_ENDPOINT_COSTS.PUBLIC_ENDPOINT_WAN_26_I2V_720P_TOKENS['5'])
  );
}

/** WAN 2.5 I2V, one clip at the service default (5 s, 720p). */
export function calculateWan25I2vTokens(): number {
  return usdToTokens(
    vendorCostUsd(PUBLIC_ENDPOINT_COSTS.PUBLIC_ENDPOINT_WAN_25_I2V_720P_TOKENS['5'])
  );
}

/** Kling v2.1 I2V Pro, one clip at the service default (5 s). */
export function calculateKlingI2vProTokens(): number {
  return usdToTokens(
    vendorCostUsd(PUBLIC_ENDPOINT_COSTS.PUBLIC_ENDPOINT_KLING_I2V_PRO_TOKENS['5'])
  );
}

/** Seedance V1.5 Pro I2V, one clip at the service default (5 s, 720p). */
export function calculateSeedanceI2vTokens(): number {
  return usdToTokens(
    vendorCostUsd(
      PUBLIC_ENDPOINT_COSTS.PUBLIC_ENDPOINT_SEEDANCE_720P_TOKENS_PER_SECOND *
        SINGLE_SHOT_RUN_SECONDS.seedanceI2v
    )
  );
}

/**
 * SORA 2 I2V, one clip at the service default (4 s, 720p).
 *
 * Four, not five. Sora's table charges the next row up for anything over 4 s, so
 * quoting this at 5 s read the 8 s row and doubled the number.
 */
export function calculateSora2I2vTokens(): number {
  return usdToTokens(vendorCostUsd(PUBLIC_ENDPOINT_COSTS.PUBLIC_ENDPOINT_SORA_I2V_TOKENS['4']));
}

/** SORA 2 Pro I2V, one clip at the service default (4 s, 720p). See above on 4 s. */
export function calculateSora2ProI2vTokens(): number {
  return usdToTokens(
    vendorCostUsd(PUBLIC_ENDPOINT_COSTS.PUBLIC_ENDPOINT_SORA_PRO_720P_TOKENS['4'])
  );
}

/** Kling O1 R2V, one clip at the service default (5 s). */
export function calculateKlingO1R2vTokens(): number {
  return usdToTokens(
    vendorCostUsd(
      PUBLIC_ENDPOINT_COSTS.PUBLIC_ENDPOINT_KLING_O1_TOKENS_PER_SECOND *
        SINGLE_SHOT_RUN_SECONDS.klingO1R2v
    )
  );
}

/**
 * FramePack on the scene route: always free.
 *
 * `POST /scene/generate` asks `is_local_scene_source()` FIRST, and FramePack
 * answers yes (`is_local` in AI_VIDEO_SOURCE_METADATA), so the route reserves
 * nothing and charges nothing -- it runs on the user's own GPU. The cloud rate
 * this used to quote (500 tokens, borrowed from the WAN 2.6 T2V 720p row) is
 * unreachable from here, and quoting it blocked users whose balance was under
 * 500 from starting a generation that costs them nothing.
 */
export function calculateFramepackTokens(): number {
  return 0;
}

/**
 * InfiniteTalk / talking heads, one request.
 *
 * Mirrors `estimate_talking_heads_tokens()` at its defaults, which is what
 * `POST /api/v1/talking-heads/generate` charges: the 720p vendor price, times the
 * safety-check allowance the route applies by default, through the talking-heads
 * markup. The cost does not vary with audio length -- the vendor prices a run,
 * not a second.
 */
export function calculateInfiniteTalkTokens(): number {
  const costUsd =
    PRICING_MISC.TALKING_HEADS_COST_USD_PER_REQUEST['720p'] *
    PRICING_MISC.TALKING_HEADS_SAFETY_CHECK_MULTIPLIER;
  return costUsdToBilledTokens(costUsd, PRICING_MISC.TALKING_HEADS_MARKUP_MULTIPLIER);
}

/**
 * Kling Motion Control, one video.
 *
 * `MotionControlRequest` deliberately carries NO duration -- Kling prices the job
 * by tier and a caller-supplied duration would be a knob that lowers a bill
 * without changing the work -- so the route charges the product ceiling, i.e. the
 * top row of the vendor table. Reading the top row rather than restating 630
 * keeps that true if the vendor adds a tier.
 */
export function calculateKlingMotionTokens(): number {
  const topRowTokenUnits = Math.max(
    ...Object.values(PUBLIC_ENDPOINT_COSTS.PUBLIC_ENDPOINT_KLING_MOTION_TOKENS)
  );
  return costUsdToBilledTokens(
    vendorCostUsd(topRowTokenUnits),
    PRICING_MISC.MOTION_CONTROL_MARKUP_MULTIPLIER
  );
}

/**
 * AI Director ceiling estimate for one clip.
 *
 * AI Director picks a provider per scene, so the quote is the worst case, which
 * the backend defines as Veo Standard at the default resolution
 * (`VideoGenerationPricing.estimate_ai_director` delegates straight to
 * `estimate_veo(quality="standard", upscale_4k=False)`). Deriving it from
 * `calculateVeoTokens` instead of restating a per-second rate is what stops the
 * two drifting: the previous hardcoded 600 tokens/s was Veo Standard's OLD price.
 */
export function calculateAiDirectorTokens(durationSeconds: number): number {
  // '1080p', not DEFAULT_VEO_CONFIG.resolution: the backend passes
  // VEO_DEFAULT_RESOLUTION unconditionally, whatever the user's own Veo panel is
  // currently set to.
  return calculateVeoTokens(durationSeconds, 'standard', '1080p');
}
